import { defineStore } from "pinia";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { storage } from "/firebaseConfig";
import { getCurrentFirebaseUser } from "@/utils/auth";

// 세션 만료 기간 (7일, 밀리초)
const SESSION_EXPIRE_DAYS = 7;
const SESSION_EXPIRE_MS = SESSION_EXPIRE_DAYS * 24 * 60 * 60 * 1000;

// 초기 상태 정의 (객체 복사를 위해 사용)
const initStore = {
  has_email: null, // boolean
  email_needs_agreement: null, // boolean
  is_email_valid: null, // boolean
  is_email_verified: null, // boolean
  email: null, // string
  nickname: null,
  auth: null,
  uid: null, // Firebase UID 추가
  profileImage: null, // 프로필 이미지 URL
  artistLikes: [], // 찜한 아티스트 목록
  liveLikes: [], // 찜한 공연 목록
  firebaseAuthEnabled: false, // Firebase Auth 활성화 상태
  loginPopup: false, // 로그인 팝업 표시 상태
  loginContext: null, // 로그인 요청 컨텍스트 ('liveLike', 'artistLike', 'likeFilter', 'liveRegister', 'artistRegister', 'communityRegister', 'recommend', 'report', 'comment', 'profile', 'general')
  nicknamePopup: false, // 닉네임 변경 팝업 표시 상태
  levelPoint: 0, // 레벨 포인트
  isBlocked: false, // 차단된 유저 여부
  lastActivityAt: null, // 마지막 활동 시간 (세션 만료 체크용)
  termsNoticeConfirmedVersion: null, // 확인한 이용약관 안내 버전
  termsNoticeConfirmedAt: null, // 이용약관 안내 확인 시각
  joinDate: null, // 가입일
  createdAt: null, // 레거시 가입일 필드
};

export const useUserStore = defineStore("user", {
  state: () => ({ ...initStore }), // 상태를 독립적으로 유지하도록 복사본 사용

  getters: {
    isLogin: (state) =>
      state.email !== null && state.email !== undefined && state.email !== "",
    // 레벨 계산 (100점마다 1레벨)
    userLevel: (state) => Math.floor(state.levelPoint / 100) + 1,
    // 현재 레벨에서의 진행도 (0-99)
    levelProgress: (state) => state.levelPoint % 100,
    // 다음 레벨까지 필요한 점수
    pointsToNextLevel: (state) => 100 - (state.levelPoint % 100),
    // 세션 만료 여부 (7일 비활성 또는 활동 기록 없음)
    isSessionExpired: (state) => {
      // 로그인 상태가 아니면 만료 아님
      if (!state.email) return false;
      // 활동 기록이 없으면 만료 (기존 유저 로그아웃)
      if (!state.lastActivityAt) return true;
      const lastActivity = new Date(state.lastActivityAt).getTime();
      const now = Date.now();
      return (now - lastActivity) > SESSION_EXPIRE_MS;
    },
  },

  actions: {
    setUserData(userData) {
      this.$state.email = userData.email;
      this.$state.nickname = userData.nickname || null;
      this.$state.auth = userData.auth || null;
      this.$state.uid = userData.uid || null;
      this.$state.profileImage = userData.profileImage || null;
      this.$state.artistLikes = userData.artistLikes || [];
      this.$state.liveLikes = userData.liveLikes || [];
      this.$state.firebaseAuthEnabled = userData.firebaseAuthEnabled || false;
      this.$state.levelPoint = userData.levelPoint || 0;
      this.$state.isBlocked = userData.isBlocked || false;
      this.$state.termsNoticeConfirmedVersion =
        userData.termsNoticeConfirmedVersion || null;
      this.$state.termsNoticeConfirmedAt =
        userData.termsNoticeConfirmedAt || null;
      this.$state.joinDate = userData.joinDate || null;
      this.$state.createdAt = userData.createdAt || null;
      // 로그인 시 활동 시간 설정
      this.$state.lastActivityAt = new Date().toISOString();
    },
    setNickname(nickname) {
      this.$state.nickname = nickname;
    },
    setAuth(auth) {
      this.$state.auth = auth || null;
    },
    setUid(uid) {
      this.$state.uid = uid;
    },
    setArtistLikes(artistLikes) {
      this.$state.artistLikes = artistLikes || [];
    },
    setLiveLikes(liveLikes) {
      this.$state.liveLikes = liveLikes || [];
    },
    addArtistLike(artistName) {
      if (!this.$state.artistLikes.includes(artistName)) {
        this.$state.artistLikes.push(artistName);
      }
    },
    removeArtistLike(artistName) {
      this.$state.artistLikes = this.$state.artistLikes.filter(
        (name) => name !== artistName
      );
    },
    addLiveLike(liveId) {
      if (!this.$state.liveLikes.includes(liveId)) {
        this.$state.liveLikes.push(liveId);
      }
    },
    removeLiveLike(liveId) {
      this.$state.liveLikes = this.$state.liveLikes.filter(
        (id) => id !== liveId
      );
    },
    setFirebaseAuthEnabled(enabled) {
      this.$state.firebaseAuthEnabled = enabled;
    },
    setProfileImage(profileImage) {
      this.$state.profileImage = profileImage;
    },
    setLevelPoint(levelPoint) {
      this.$state.levelPoint = levelPoint || 0;
    },
    addLevelPoint(points) {
      this.$state.levelPoint += points;
    },
    setBlocked(isBlocked) {
      this.$state.isBlocked = isBlocked || false;
    },
    setTermsNoticeConfirmation(version, confirmedAt = new Date().toISOString()) {
      this.$state.termsNoticeConfirmedVersion = version || null;
      this.$state.termsNoticeConfirmedAt = confirmedAt || null;
    },
    // 로그인 컨텍스트와 함께 로그인 팝업 표시
    showLoginPopup(context = 'general') {
      this.$state.loginContext = context;
      this.$state.loginPopup = true;
    },
    // 로그인 팝업 닫기 및 컨텍스트 초기화
    closeLoginPopup() {
      this.$state.loginPopup = false;
      this.$state.loginContext = null;
    },
    async logOut() {
      // 토큰 삭제
      const { clearTokens } = await import("@/utils/api");
      clearTokens();
      
      // 상태 초기화
      this.$reset();
    },
    // 마지막 활동 시간 업데이트
    updateActivity() {
      if (this.$state.email) {
        this.$state.lastActivityAt = new Date().toISOString();
      }
    },
    // 세션 만료 체크 및 자동 로그아웃
    async checkSessionExpiry() {
      if (this.isSessionExpired) {
        console.warn("세션이 만료되어 자동 로그아웃됩니다. (7일 비활성)");
        
        // 토큰 삭제
        const { clearTokens } = await import("@/utils/api");
        clearTokens();
        
        this.$reset();
        return true; // 만료됨
      }
      // 세션이 유효하면 활동 시간 업데이트
      this.updateActivity();
      return false; // 유효함
    },
    // Firestore에서 사용자 정보를 가져와서 store와 동기화
    async syncUserData() {
      if (!this.$state.email) {
        return; // 로그인되지 않은 경우
      }

      try {
        const { getUserInfo } = await import("@/utils/read/user");
        const userData = await getUserInfo(this.$state.email);

        if (userData) {
          const firebaseUser = getCurrentFirebaseUser();

          // Firebase Storage에서 프로필 이미지 로드
          let profileImage = null;
          if (userData.img) {
            try {
              const imageRef = storageRef(storage, `profile/${userData.img}`);
              profileImage = await getDownloadURL(imageRef);
            } catch (error) {
              console.warn("프로필 이미지 로드 실패:", error);
              profileImage = null;
            }
          }

          // 현재 store의 데이터와 비교하여 변경사항이 있으면 업데이트
          const newUserData = {
            email: this.$state.email,
            uid: firebaseUser?.uid || this.$state.uid || null,
            nickname: userData.nickname || this.$state.email.split("@")[0],
            auth: userData.auth || null,
            profileImage: profileImage,
            artistLikes: userData.artistLikes || [],
            liveLikes: userData.liveLikes || [],
            firebaseAuthEnabled: !!firebaseUser,
            levelPoint: userData.levelPoint || 0,
            isBlocked: userData.isBlocked || false,
            termsNoticeConfirmedVersion:
              userData.termsNoticeConfirmedVersion || null,
            termsNoticeConfirmedAt: userData.termsNoticeConfirmedAt || null,
            joinDate: userData.joinDate || null,
            createdAt: userData.createdAt || null,
          };

          // 데이터가 변경되었는지 확인하고 업데이트
          if (
            this.$state.nickname !== newUserData.nickname ||
            this.$state.auth !== newUserData.auth ||
            this.$state.profileImage !== newUserData.profileImage ||
            JSON.stringify(this.$state.artistLikes) !== JSON.stringify(newUserData.artistLikes) ||
            JSON.stringify(this.$state.liveLikes) !== JSON.stringify(newUserData.liveLikes) ||
            this.$state.levelPoint !== newUserData.levelPoint ||
            this.$state.isBlocked !== newUserData.isBlocked ||
            this.$state.termsNoticeConfirmedVersion !== newUserData.termsNoticeConfirmedVersion ||
            this.$state.termsNoticeConfirmedAt !== newUserData.termsNoticeConfirmedAt ||
            this.$state.joinDate !== newUserData.joinDate ||
            this.$state.createdAt !== newUserData.createdAt
          ) {
            this.setUserData(newUserData);
          }
        } else {
          console.warn("사용자 문서를 찾을 수 없습니다:", this.$state.email);
        }
      } catch (error) {
        console.error("사용자 정보 동기화 실패:", error);
      }
    },
  },
  persist: true, // 자동으로 localStorage에 저장됨
});
